// Imports
import { faCircleCheck, faCircleQuestion, faCircleXmark, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tippy from '@tippyjs/react';
import React, { useCallback, useEffect } from 'react';
import AnimateHeight from 'react-animate-height';
import styled from 'styled-components/macro';
import tippyProps from '../../../../../../shareables/foundation/front-end/utils/tippy-props';
import usePagination, { Options } from '../../../../../../shareables/foundation/front-end/utils/use-pagination';
import theme from '../../../../../../shareables/theme';
import { GetOccurrenceResponse } from '../../../../../../shareables/types/api/main/website/page/type/202/event/occurrence/get';
import Table from '../shared-components/table';
import SortableTableHeader from './sortable-table-header';


// Map responses to icons and strings
const responseMap: Record<string, { icon: IconDefinition; color: string; text: string }> = {
	GOING: {
		icon: faCircleCheck,
		color: theme.colors.success,
		text: 'Going',
	},
	MAYBE: {
		icon: faCircleQuestion,
		color: theme.colors.yellow,
		text: 'Maybe',
	},
	NOT_GOING: {
		icon: faCircleXmark,
		color: theme.colors.danger,
		text: 'Not going',
	},
};


// Use styled components
const Wrapper = styled(AnimateHeight)`
	> div {
		padding-top: 1rem;
	}
`;


// Define the accepted props
interface Props {
	responses: GetOccurrenceResponse['rsvps'];
	isOpen: boolean;
}


// Function component
const ResponsesTable: React.FC<Props> = ({ responses, isOpen }) => {
	// Use callback
	const memoizedSortFunction = useCallback<
		NonNullable<Options<GetOccurrenceResponse['rsvps'][0]>['memoizedSortFunction']>
	>((a, b, sorting) => {
		// Throw error if column provided is not an expected key
		const isObjectKey = (key: string | keyof typeof a): key is keyof typeof a =>
			Object.prototype.hasOwnProperty.call(a, key);
		
		if (!isObjectKey(sorting.column)) {
			throw new Error('Column provided is not an expected key');
		}
		
		
		// Sort
		const initialSort =
			sorting.direction === 'ascending'
				? a[sorting.column].localeCompare(b[sorting.column])
				: b[sorting.column].localeCompare(a[sorting.column]);
		
		
		// Handle RSVP column fallback
		if (sorting.column === 'response' && initialSort === 0) {
			return b.responded.localeCompare(a.responded);
		}
		
		
		// Return result of the initial sort
		return initialSort;
	}, []);
	
	
	// Use pagination
	const {
		items: sortedResponses,
		setItems: setSortedResponses,
		sorting,
		setSortingColumn,
		paginationComponent,
	} = usePagination<GetOccurrenceResponse['rsvps'][0]>({
		cacheStorageKey: 'event-rsvp-response',
		pluralDescriptor: 'responses',
		pageSizes: [10],
		memoizedSortFunction: memoizedSortFunction,
		initialSorting: {
			column: 'response',
			direction: 'ascending',
		},
	});
	
	
	// Update the responses
	useEffect(() => {
		setSortedResponses(
			[...responses].sort((a, b) => memoizedSortFunction(a, b, { column: 'response', direction: 'ascending' }))
		);
	}, [responses, setSortedResponses, memoizedSortFunction]);
	
	
	// Return JSX
	return (
		<Wrapper height={isOpen ? 'auto' : 0.01}>
			<Table>
				<thead>
					<tr>
						<SortableTableHeader
							onClick={() => setSortingColumn('name')}
							indicator={sorting?.column === 'name' ? sorting?.direction : undefined}
						>
							Name
						</SortableTableHeader>
						<SortableTableHeader
							style={{ textAlign: 'center' }}
							onClick={() => setSortingColumn('response')}
							indicator={sorting?.column === 'response' ? sorting?.direction : undefined}
						>
							RSVP
						</SortableTableHeader>
					</tr>
				</thead>
				<tbody>
					{sortedResponses?.map((response, index) => (
						<tr key={index}>
							<td>{response.name}</td>
							<td style={{ textAlign: 'center' }}>
								<Tippy
									{...tippyProps}
									content={new Date(`${response.responded}Z`).toLocaleDateString(undefined, {
										month: 'short',
										day: 'numeric',
										year: 'numeric',
										hour: 'numeric',
										minute: '2-digit',
									})}
								>
									<span
										aria-label={`${response.name} responded “${response.response.replace(/_/g, ' ').toLocaleLowerCase()}”`}
									>
										<FontAwesomeIcon
											icon={responseMap[response.response].icon}
											color={responseMap[response.response].color}
										/>{' '}
										{responseMap[response.response].text}
									</span>
								</Tippy>
							</td>
						</tr>
					))}
				</tbody>
			</Table>
			
			{paginationComponent}
		</Wrapper>
	);
};

export default ResponsesTable;
